import { Link, generatePath } from 'react-router-dom';
import ROUTES from 'constants/routes';
import { formatGraphValue } from './graphData';
import type { GraphEdge } from './types';
import styles from './NodeGraphPanel.module.scss';

interface Props {
	edges: GraphEdge[];
	selectedEdge?: string;
	onSelect: (id: string) => void;
}

function ConnectionList({ edges, selectedEdge, onSelect }: Props): JSX.Element {
	const selected = edges.find((edge) => edge.id === selectedEdge);
	return (
		<div className={styles.connections} data-testid="node-graph-connections">
			<div className={styles.connectionList} aria-label="Connections">
				{edges.map((edge) => (
					<button
						key={edge.id}
						type="button"
						className={styles.connection}
						data-tone={edge.tone}
						aria-pressed={edge.id === selectedEdge}
						onClick={() => onSelect(edge.id)}
						data-testid="node-graph-connection"
					>
						<span className={styles.connectionName}>
							{edge.sourceName} → {edge.targetName}
						</span>
						<span className={styles.connectionMeta}>
							{`${edge.scope || 'Unspecified scope'} · ${formatGraphValue(edge.calls)} calls · ${formatGraphValue(edge.rate, '%')} errors`}
						</span>
					</button>
				))}
			</div>
			{selected && (
				<section
					className={styles.details}
					aria-label="Selected connection"
					aria-live="polite"
					data-testid="node-graph-details"
				>
					<strong>
						{selected.sourceName} → {selected.targetName}
					</strong>
					<dl>
						<div>
							<dt>Scope</dt>
							<dd>{selected.scope || '—'}</dd>
						</div>
						<div>
							<dt>Calls</dt>
							<dd>{formatGraphValue(selected.calls)}</dd>
						</div>
						<div>
							<dt>Failed calls</dt>
							<dd>{formatGraphValue(selected.errors)}</dd>
						</div>
						<div>
							<dt>Error rate</dt>
							<dd>{formatGraphValue(selected.rate, '%')}</dd>
						</div>
						<div>
							<dt>Latency</dt>
							<dd>{formatGraphValue(selected.latency, ' ms')}</dd>
						</div>
					</dl>
					{selected.traceId ? (
						<Link
							to={generatePath(ROUTES.TRACE_DETAIL, { id: selected.traceId })}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.traceLink}
							data-testid="node-graph-trace-link"
						>
							Open failing trace ↗
						</Link>
					) : (
						<p className={styles.hint}>No failing trace in this result.</p>
					)}
				</section>
			)}
		</div>
	);
}

export default ConnectionList;
